import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { useCallback, useMemo } from 'react';
import {
  type FieldValues,
  type Path,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  Button,
  DatePicker,
  Form,
  FormField,
  FormFieldCurrencyInput,
  FormFieldDropdownSingleSelect,
  FormFieldInput,
  FormFieldToggle,
  Icon,
  type IconName,
  Tooltip,
} from '~/chords';
import { Breadcrumbs } from '~/chords/Breadcrumbs';

import { useAdvertisers } from '../../api/useAdvertisers';
import { formatCurrency } from '../../utils/formatters';
import {
  type CreateCampaignFormValues,
  createCampaignSchema,
} from './createCampaignSchema';

const DEFAULT_VALUES: CreateCampaignFormValues = {
  campaignStartDate: '',
  campaignEndDate: '',
  advertiser: '',
  artist: '',
  track: '',
  isrc: '',
  spotifyUrl: '',
  tiktokLink: '',
  instagramLink: '',
  clippingEnabled: true,
  clippingBudget: undefined,
  clippingPurchaseOrder: '',
  influencerEnabled: true,
  influencerBudget: undefined,
  influencerPurchaseOrder: '',
};

function InfoTooltip({ content }: { content: string }) {
  return (
    <Tooltip content={content} size="sm">
      <span>
        <Icon type="circle-info" size="sm" />
      </span>
    </Tooltip>
  );
}

InfoTooltip.displayName = 'InfoTooltip';

function SectionHeading({
  icon,
  children,
}: {
  icon: IconName;
  children: React.ReactNode;
}) {
  return (
    <h2 className="flex items-center gap-sm text-headline-sm font-semibold">
      <Icon type={icon} size="md" />
      {children}
    </h2>
  );
}

SectionHeading.displayName = 'SectionHeading';

function FormFieldDatePicker<TFormFields extends FieldValues>({
  name,
  label,
  placeholder,
  minDate,
}: {
  name: Path<TFormFields>;
  label: string;
  placeholder?: string;
  minDate?: Date;
}) {
  const { field } = useController<TFormFields>({ name });

  const dateValue = field.value ? parseISO(field.value as string) : undefined;

  const handleApply = useCallback(
    (date: Date) => {
      field.onChange(format(date, 'yyyy-MM-dd'));
    },
    [field],
  );

  return (
    <FormField<TFormFields> name={name} label={label} labelAsSpan>
      <DatePicker
        value={dateValue}
        onApply={handleApply}
        placeholder={placeholder}
        minDate={minDate}
        allowFutureDates
      />
    </FormField>
  );
}

FormFieldDatePicker.displayName = 'FormFieldDatePicker';

export function CreateCampaignPage() {
  const { t } = useTranslation('automated-creator-campaign');
  const navigate = useNavigate();
  const location = useLocation();
  const { advertisers, isLoading: isLoadingAdvertisers } = useAdvertisers();

  const restoredValues = (
    location.state as { formValues?: CreateCampaignFormValues } | null
  )?.formValues;

  const advertiserOptions = useMemo(
    () => advertisers.map((name) => ({ value: name, label: name })),
    [advertisers],
  );

  const formHook = useForm<CreateCampaignFormValues>({
    resolver: zodResolver(createCampaignSchema),
    defaultValues: restoredValues ?? DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const clippingEnabled = useWatch({
    control: formHook.control,
    name: 'clippingEnabled',
  });
  const influencerEnabled = useWatch({
    control: formHook.control,
    name: 'influencerEnabled',
  });
  const clippingBudget = useWatch({
    control: formHook.control,
    name: 'clippingBudget',
  });
  const influencerBudget = useWatch({
    control: formHook.control,
    name: 'influencerBudget',
  });

  const totalSpend = useMemo(() => {
    const clipping = clippingEnabled ? (clippingBudget ?? 0) : 0;
    const influencer = influencerEnabled ? (influencerBudget ?? 0) : 0;
    const total = clipping + influencer;

    return total > 0 ? formatCurrency(total * 100) : '--';
  }, [clippingEnabled, influencerEnabled, clippingBudget, influencerBudget]);

  const handleSubmit = (values: CreateCampaignFormValues) => {
    navigate('/marketing/automated-creator-campaigns/new/review', {
      state: { formValues: values },
    });
  };

  return (
    <div className="max-w-[2048px] p-xl">
      <Breadcrumbs.Root>
        <Breadcrumbs.Item to="/marketing">
          {t('breadcrumb.marketing')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item to="/marketing/automated-creator-campaigns">
          {t('breadcrumb.current')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item>{t('createCampaign.breadcrumb')}</Breadcrumbs.Item>
      </Breadcrumbs.Root>

      <h1 className="mt-md text-display-sm font-bold">
        {t('createCampaign.title')}
      </h1>

      <Form
        formHook={formHook}
        onSubmit={handleSubmit}
        schema={createCampaignSchema}
      >
        <section className="mt-xl rounded-sm border bg-emphasis-default border-emphasis-subtle">
          {/* Campaign details + Track details */}
          <div className="grid grid-cols-2 gap-xl p-lg">
            <div className="flex flex-col gap-lg border-r pr-xl border-emphasis-subtle">
              <SectionHeading icon="calendar-check">
                {t('createCampaign.sections.campaignDetails')}
              </SectionHeading>

              <div className="grid grid-cols-2 gap-md">
                <FormFieldDatePicker<CreateCampaignFormValues>
                  name="campaignStartDate"
                  label={t('createCampaign.fields.startDate')}
                  placeholder={t('createCampaign.fields.startDatePlaceholder')}
                />
                <FormFieldDatePicker<CreateCampaignFormValues>
                  name="campaignEndDate"
                  label={t('createCampaign.fields.endDate')}
                  placeholder={t('createCampaign.fields.endDatePlaceholder')}
                />
              </div>

              <FormFieldDropdownSingleSelect<unknown, CreateCampaignFormValues>
                name="advertiser"
                label={t('createCampaign.fields.advertiser')}
                placeholder={t('createCampaign.fields.advertiserPlaceholder')}
                options={advertiserOptions}
                disabled={isLoadingAdvertisers}
              />
            </div>

            <div className="flex flex-col gap-lg">
              <SectionHeading icon="music">
                {t('createCampaign.sections.trackDetails')}
              </SectionHeading>

              <FormFieldInput<CreateCampaignFormValues>
                name="artist"
                label={t('createCampaign.fields.artist')}
                placeholder={t('createCampaign.fields.artistPlaceholder')}
              />

              <FormFieldInput<CreateCampaignFormValues>
                name="track"
                label={t('createCampaign.fields.track')}
                placeholder={t('createCampaign.fields.trackPlaceholder')}
              />

              <FormFieldInput<CreateCampaignFormValues>
                name="isrc"
                label={t('createCampaign.fields.isrc')}
                placeholder={t('createCampaign.fields.isrcPlaceholder')}
                labelAddon={
                  <InfoTooltip content={t('createCampaign.tooltips.isrc')} />
                }
              />
            </div>
          </div>

          <hr className="border-emphasis-subtle" />

          {/* Links */}
          <div className="flex flex-col gap-lg p-lg">
            <SectionHeading icon="link">
              {t('createCampaign.sections.links')}
            </SectionHeading>

            <FormFieldInput<CreateCampaignFormValues>
              name="spotifyUrl"
              label={t('createCampaign.fields.spotifyUrl')}
              placeholder={t('createCampaign.fields.spotifyUrlPlaceholder')}
            />

            <FormFieldInput<CreateCampaignFormValues>
              name="tiktokLink"
              label={t('createCampaign.fields.tiktokLink')}
              placeholder={t('createCampaign.fields.tiktokLinkPlaceholder')}
            />

            <FormFieldInput<CreateCampaignFormValues>
              name="instagramLink"
              label={t('createCampaign.fields.instagramLink')}
              placeholder={t('createCampaign.fields.instagramLinkPlaceholder')}
            />
          </div>

          <hr className="border-emphasis-subtle" />

          {/* Channel and budget */}
          <div className="flex flex-col gap-lg p-lg">
            <SectionHeading icon="circle-dollar">
              {t('createCampaign.sections.channelAndBudget')}
            </SectionHeading>

            {/* Row: channel labels + toggles */}
            <div className="grid grid-cols-3 gap-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-xs">
                  <span className="text-body-md font-medium">
                    {t('createCampaign.fields.clippingCampaign')}
                  </span>
                  <InfoTooltip
                    content={t('createCampaign.tooltips.clippingCampaign')}
                  />
                </div>
                <FormFieldToggle<CreateCampaignFormValues>
                  name="clippingEnabled"
                  label=""
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-xs">
                  <span className="text-body-md font-medium">
                    {t('createCampaign.fields.influencerCampaign')}
                  </span>
                  <InfoTooltip
                    content={t('createCampaign.tooltips.influencerCampaign')}
                  />
                </div>
                <FormFieldToggle<CreateCampaignFormValues>
                  name="influencerEnabled"
                  label=""
                />
              </div>
              <span className="text-body-md font-medium">
                {t('createCampaign.fields.totalSpend')}
              </span>
            </div>

            {/* Row: budget inputs + total */}
            <div className="grid grid-cols-3 gap-lg">
              <FormFieldCurrencyInput<CreateCampaignFormValues>
                name="clippingBudget"
                label=""
                prefix="$ "
                placeholder={t('createCampaign.fields.clippingMinimumSpend')}
                disabled={!clippingEnabled}
                decimalsLimit={2}
              />
              <FormFieldCurrencyInput<CreateCampaignFormValues>
                name="influencerBudget"
                label=""
                prefix="$ "
                placeholder={t('createCampaign.fields.influencerMinimumSpend')}
                disabled={!influencerEnabled}
                decimalsLimit={2}
              />
              <div className="flex h-[35px] items-center text-headline-sm font-semibold">
                {totalSpend}
              </div>
            </div>

            {/* Row: purchase order inputs */}
            <div className="grid grid-cols-3 gap-lg">
              <FormFieldInput<CreateCampaignFormValues>
                name="clippingPurchaseOrder"
                label={t('createCampaign.fields.clippingPurchaseOrder')}
                placeholder={t(
                  'createCampaign.fields.purchaseOrderNumberPlaceholder',
                )}
                disabled={!clippingEnabled}
              />
              <FormFieldInput<CreateCampaignFormValues>
                name="influencerPurchaseOrder"
                label={t('createCampaign.fields.influencerPurchaseOrder')}
                placeholder={t(
                  'createCampaign.fields.purchaseOrderNumberPlaceholder',
                )}
                disabled={!influencerEnabled}
              />
              <div />
            </div>
          </div>
        </section>

        {/* Actions */}
        <div className="mt-xl flex justify-end">
          <Button type="submit" variant="primary" size="large">
            {t('createCampaign.actions.next')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

CreateCampaignPage.displayName = 'CreateCampaignPage';
